// Port von frontend/src/lib/api/client.ts (SvelteKit) — 2026-09-04. Die Datei
// war frameworkneutral (reines fetch + TypeScript) und wird hier 1:1 gefuehrt;
// Aenderungen bitte in BEIDEN Fassungen nachziehen, solange beide Frontends
// existieren. Einzige Ergaenzung: `onTool` in streamMessage (tool_start /
// tool_result), damit die Oberflaeche „durchsucht Befunde …" zeigen kann.
const API = '/api';

// ─── Auth ──────────────────────────────────────────────────────────────

export type ChatMode = 'patient' | 'patient_md' | 'praxis' | 'wissen' | 'general' | 'review';
export const ALL_CHAT_MODES: ChatMode[] = ['patient', 'patient_md', 'praxis', 'wissen', 'general'];

export type AuthUser = {
  id: string;
  email: string;
  username: string | null;
  name: string;
  role: 'admin' | 'arzt' | 'mpa' | 'pflege' | 'user';
  active: boolean;
  allowed_modes: ChatMode[];
  created_at: string;
  last_login_at: string | null;
};

export async function login(username: string, password: string): Promise<AuthUser> {
  const r = await fetch(`${API}/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ username, password })
  });
  if (!r.ok) {
    const detail = await r.json().catch(() => ({}));
    throw new Error(detail.detail ?? `Login fehlgeschlagen (${r.status})`);
  }
  return r.json();
}

export async function logout(): Promise<void> {
  await fetch(`${API}/auth/logout`, { method: 'POST', credentials: 'include' });
}

export async function getMe(): Promise<AuthUser | null> {
  const r = await fetch(`${API}/auth/me`, { credentials: 'include' });
  if (!r.ok) return null;
  return r.json();
}


// ─── User-Verwaltung ──────────────────────────────────────────────────

export async function adminListUsers(): Promise<AuthUser[]> {
  const r = await fetch(`${API}/users`);
  if (!r.ok) throw new Error(`listUsers ${r.status}`);
  return r.json();
}

export type UserPickerItem = { id: string; username: string | null; name: string; role: string };

export async function listUsersForPicker(): Promise<UserPickerItem[]> {
  const r = await fetch(`${API}/users/picker`, { credentials: 'include' });
  if (!r.ok) return [];
  return r.json();
}

export async function adminCreateUser(payload: {
  username: string; email: string; name: string; role: string; password: string;
  allowed_modes?: ChatMode[] | null;
}): Promise<AuthUser> {
  const r = await fetch(`${API}/users`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload)
  });
  if (!r.ok) {
    const d = await r.json().catch(() => ({}));
    throw new Error(d.detail ?? `createUser ${r.status}`);
  }
  return r.json();
}

export async function adminPatchUser(
  userId: string,
  payload: {
    name?: string; email?: string; role?: string; password?: string;
    active?: boolean; allowed_modes?: ChatMode[] | null;
  }
): Promise<AuthUser> {
  const r = await fetch(`${API}/users/${userId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload)
  });
  if (!r.ok) {
    const d = await r.json().catch(() => ({}));
    throw new Error(d.detail ?? `patchUser ${r.status}`);
  }
  return r.json();
}

export async function adminDeleteUser(userId: string): Promise<void> {
  const r = await fetch(`${API}/users/${userId}`, { method: 'DELETE' });
  if (!r.ok) {
    const d = await r.json().catch(() => ({}));
    throw new Error(d.detail ?? `deleteUser ${r.status}`);
  }
}

export type Chat = {
  id: string;
  title: string;
  mode: 'patient' | 'patient_md' | 'praxis' | 'wissen' | 'general' | 'review';
  patient_id: string | null;
  model: string;
  archived: boolean;
  summary?: string | null;
  parent_chat_id?: string | null;
  created_at: string;
  updated_at: string;
  message_count: number;
  // Share-Indikatoren (vom Backend für jeden Chat befüllt)
  shared_with_me_by?: string | null;   // wenn !=null: dieser Chat ist nicht meiner
  can_write?: boolean;                  // false bei read-only-Share
  is_shared?: boolean;                  // ich bin Owner und habe ihn geteilt
  /** Das Modell schreibt noch — letzte Nachricht ist die Frage ohne Antwort. */
  wartet_auf_antwort?: boolean;
};

export type Source =
  | { patient_id: string; filename: string }
  | { url: string; title: string }
  | { image_id: string }
  | { review_ref: string; pdf_url: string; title: string }
  | { kb_slug: string; pdf_url: string; title: string };

export type Message = {
  id: string;
  role: 'user' | 'assistant' | 'system' | 'tool';
  content: string;
  sources?: Source[] | null;
  created_at: string;
  // Telemetrie — nur für Admins/Ärzte sichtbar im Debug-Panel
  tokens_in?: number | null;
  tokens_out?: number | null;
  duration_ms?: number | null;
  tool_calls?: any | null;
};

export async function listChats(archived = false): Promise<Chat[]> {
  const r = await fetch(`${API}/chats?archived=${archived}`);
  if (!r.ok) throw new Error(`listChats ${r.status}`);
  return r.json();
}

export async function patchChat(
  chatId: string,
  payload: { title?: string; archived?: boolean }
): Promise<Chat> {
  const r = await fetch(`${API}/chats/${chatId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload)
  });
  if (!r.ok) throw new Error(`patchChat ${r.status}`);
  return r.json();
}

export async function deleteChatsBulk(scope: 'archived' | 'all'): Promise<{ deleted: number }> {
  const r = await fetch(`${API}/chats?scope=${scope}`, { method: 'DELETE' });
  if (!r.ok) throw new Error(`deleteChatsBulk ${r.status}`);
  return r.json();
}

export async function createChat(payload: {
  title?: string;
  mode?: 'patient' | 'patient_md' | 'praxis' | 'wissen' | 'general' | 'review';
  patient_id?: string | null;
  model?: string;
  parent_chat_id?: string | null;
  prompt_seed?: string | null;
}): Promise<Chat> {
  const r = await fetch(`${API}/chats`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload)
  });
  if (!r.ok) throw new Error(`createChat ${r.status}`);
  return r.json();
}

export async function deriveTopicFromMessage(chatId: string, messageId: string): Promise<{
  topic: string;
  patient_hint: string;
  parent_chat_id: string;
  patient_id: string | null;
}> {
  const r = await fetch(`${API}/chats/${chatId}/messages/${messageId}/derive-topic`, {
    method: 'POST', credentials: 'include',
  });
  if (!r.ok) throw new Error(`deriveTopic ${r.status}`);
  return r.json();
}

export async function getMessages(chatId: string): Promise<Message[]> {
  const r = await fetch(`${API}/chats/${chatId}/messages`);
  if (!r.ok) throw new Error(`getMessages ${r.status}`);
  return r.json();
}

export async function deleteChat(chatId: string): Promise<void> {
  const r = await fetch(`${API}/chats/${chatId}`, { method: 'DELETE', credentials: 'include' });
  // Fehler NICHT schlucken (08.10.2026): vorher passierte bei einem abgelehnten Loeschen einfach nichts.
  if (!r.ok) {
    const d = await r.json().catch(() => ({}));
    throw new Error(d.detail ?? `Löschen fehlgeschlagen (${r.status})`);
  }
}

export type Patient = {
  patient_id: string;
  vorname: string | null;
  nachname: string | null;
  geburtsdatum: string | null;
  /** AHV-Nummer aus der siMed-Stammliste, Format 756.1234.5678.90. */
  ahv?: string | null;
  station: string | null;
  tentative: boolean;
  file_count: number;
  doc_count?: number;
  /** false = Akte liegt noch nicht vor, nur der Stammdatensatz. */
  importiert?: boolean;
  /** Letzte erfolgreiche Aktualisierung aus siMed (Unix-Sekunden), null = unbekannt. */
  simed_zuletzt?: number | null;
};

export type PatientFile = {
  name: string;
  typ: string | null;
  datum: string | null;
  fachgebiet: string | null;
  /** Aus dem Frontmatter der Wiki-Struktur — sagt, WAS das Dokument ist. */
  titel?: string | null;
  kategorie?: string | null;
  dateiname?: string | null;
  diagnosen: string[];
  behandlung: string[];
  size: number;
  /** Woher der erkannte Text stammt: textebene | ocr | office | mail. */
  textquelle?: string | null;
  /** Wie viele Zeichen erkannt wurden. */
  zeichen?: number | null;
  /** Anzeigbare PDF-Fassung eines Word-Dokuments (Dateiname unter _attachments/). */
  vorschau?: string | null;
  has_attachment: boolean;
  attachment: string | null;   // echter Dateiname unter _attachments/
};

export type PatientDetail = {
  patient_id: string;
  stammdaten: Record<string, any>;
  files: PatientFile[];
};

export type PatientenSeite = {
  patients: Patient[];
  gesamt: number;
  /** Beide Zahlen kommen IMMER mit — damit beide Reiter beschriftet werden
   *  koennen, ohne den Server ein zweites Mal zu fragen. */
  gesamt_importiert?: number;
  gesamt_offen?: number;
  bereich?: Bereich;
  offset: number;
  limit: number;
  hat_mehr: boolean;
};

/** Welcher Teil der Patientenschaft gemeint ist. */
export type Bereich = 'importiert' | 'offen' | 'alle';

/** Patientenliste — gesucht und portioniert wird auf dem SERVER.
 *
 * Vorher kam die komplette Liste (bei 11.346 Patienten 2,1 MB) und der Browser
 * filterte. Ueber VPN und auf einem Tablet war das die spuerbarste Bremse der
 * Anwendung.
 */
export async function listPatientsPage(opts: {
  bereich?: Bereich; alle?: boolean; q?: string; limit?: number; offset?: number;
  sort?: string; richtung?: 'auf' | 'ab';
  signal?: AbortSignal;
} = {}): Promise<PatientenSeite> {
  const p = new URLSearchParams();
  if (opts.sort) { p.set('sort', opts.sort); p.set('richtung', opts.richtung ?? 'auf'); }
  if (opts.bereich) p.set('bereich', opts.bereich);
  if (opts.alle) p.set('alle', 'true');
  if (opts.q) p.set('q', opts.q);
  p.set('limit', String(opts.limit ?? 50));
  p.set('offset', String(opts.offset ?? 0));
  const r = await fetch(`/api/patients?${p.toString()}`, { signal: opts.signal });
  if (!r.ok) return { patients: [], gesamt: 0, gesamt_importiert: 0, gesamt_offen: 0,
                      offset: 0, limit: 50, hat_mehr: false };
  return r.json();
}

/** Nur die Eintraege — fuer Aufrufer, die keine Seiten brauchen. */
export async function listPatients(alle = false): Promise<Patient[]> {
  const d = await listPatientsPage({ alle, limit: 200 });
  return d.patients;
}

/** Akte aus siMed anfordern — legt nur einen Auftrag an, geholt wird sie im Hintergrund. */
export async function akteAusSimed(pid: string): Promise<any> {
  const r = await fetch(`/api/patients/${encodeURIComponent(pid)}/aus-simed`, {
    method: 'POST',
  });
  if (!r.ok) throw new Error('Auftrag konnte nicht angelegt werden');
  return r.json();
}

/** Alle laufenden/wartenden siMed-Auftraege. */
/** Alle fehlgeschlagenen siMed-Importe auf einmal wiederholen. */
export async function fehlgeschlageneErneut(): Promise<{ erneut: number; pids: string[] }> {
  return _j(await fetch(`${API}/patients/_fehlgeschlagene-erneut`, { method: 'POST', credentials: 'include' }),
            'fehlgeschlageneErneut');
}
export async function patientenAuftraege(): Promise<{ auftraege: Record<string, any> }> {
  const r = await fetch(`/api/patients/_auftraege`);
  if (!r.ok) return { auftraege: {} };
  return r.json();
}

export type SimedStand = {
  status: 'wartend' | 'laeuft' | 'fertig' | 'fehler' | null;
  /** Letzte erfolgreiche Aktualisierung aus siMed (Unix-Sekunden), null = unbekannt. */
  zuletzt: number | null;
  naechster_versuch: number | null;
  meldung: string;
  darf_anfordern: boolean;
};

export async function simedStand(pid: string): Promise<SimedStand | null> {
  const r = await fetch(`/api/patients/${encodeURIComponent(pid)}/simed-stand`);
  if (!r.ok) return null;
  return r.json();
}

export async function getPatient(pid: string): Promise<PatientDetail | null> {
  const r = await fetch(`/api/patients/${encodeURIComponent(pid)}`);
  if (!r.ok) return null;
  return r.json();
}

export async function getPatientFile(pid: string, filename: string): Promise<string | null> {
  const r = await fetch(`/api/patients/${encodeURIComponent(pid)}/files/${encodeURIComponent(filename)}`);
  if (!r.ok) return null;
  const data = await r.json();
  return data.content;
}

// ─── Patienten-Cockpit (strukturierte Übersicht) ──────────────────────

export type CockpitDiagnose = {
  text: string; seit: string; wichtig: boolean;
  /** Wörtliches Zitat aus dem Dokument, das diese Diagnose belegt. */
  beleg?: string; beleg_quelle?: string; beleg_datum?: string;
};
export type CockpitMedi = { medi: string; dosierung: string; bemerkung: string; seit: string };
export type CockpitAllergie = { allergie: string; staerke: string; bemerkung: string };
export type CockpitLabor = {
  parameter: string; wert: string; einheit: string;
  referenz: string; datum: string; flag: 'hoch' | 'tief' | 'normal' | '';
};
export type CockpitKontakt = { datum: string; fachgebiet: string; typ: string };

export type PatientCockpit = {
  patient_id: string;
  has_struktur: boolean;
  diagnosen: CockpitDiagnose[];
  diagnosen_weitere: number;
  /** Keine Diagnose ist als wichtig markiert — gezeigt sind die neuesten. */
  diagnosen_ungekennzeichnet?: boolean;
  diagnosen_total: number;
  dauermedikation: CockpitMedi[];
  allergien: CockpitAllergie[];
  labor_aktuell: CockpitLabor[];
  letzte_kontakte: CockpitKontakt[];
};

export async function getPatientCockpit(pid: string): Promise<PatientCockpit | null> {
  const r = await fetch(`/api/patients/${encodeURIComponent(pid)}/cockpit`);
  if (!r.ok) return null;
  return r.json();
}

export function patientAttachmentUrl(pid: string, filename: string): string {
  return `/api/patients/${encodeURIComponent(pid)}/attachments/${encodeURIComponent(filename)}`;
}

export async function listModels(): Promise<{ name: string; size_gb: number }[]> {
  const r = await fetch(`${API}/chats/models`);
  if (!r.ok) return [];
  const data = await r.json();
  return data.models || [];
}

// ─── Admin ────────────────────────────────────────────────────────────

export type AdminSetting = {
  key: string;
  value: any;
  default: any;
  is_override: boolean;
};

export type AdminModel = {
  name: string;
  size_gb: number;
  modified: string | null;
  family: string | null;
  params: string | null;
};

export type AdminTool = {
  name: string;
  description: string;
  required: string[];
};

export type AdminAuditEntry = {
  id: string;
  action: string;
  target_type: string | null;
  target_id: string | null;
  details: Record<string, any> | null;
  user_id: string | null;
  user_name?: string | null;
  user_username?: string | null;
  created_at: string;
};

export async function adminListSettings(): Promise<AdminSetting[]> {
  const r = await fetch(`${API}/admin/settings`);
  if (!r.ok) throw new Error(`admin/settings ${r.status}`);
  const data = await r.json();
  return data.settings ?? [];
}

export async function adminPutSetting(key: string, value: any): Promise<boolean> {
  const r = await fetch(`${API}/admin/settings/${encodeURIComponent(key)}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ value })
  });
  return r.ok;
}

export async function adminResetSetting(key: string): Promise<boolean> {
  const r = await fetch(`${API}/admin/settings/${encodeURIComponent(key)}`, { method: 'DELETE' });
  return r.ok;
}

export async function adminListModels(): Promise<AdminModel[]> {
  const r = await fetch(`${API}/admin/models`);
  if (!r.ok) return [];
  const data = await r.json();
  return data.models ?? [];
}

export async function adminListTools(): Promise<Record<string, AdminTool[]>> {
  const r = await fetch(`${API}/admin/tools`);
  if (!r.ok) return {};
  const data = await r.json();
  return data.tools_by_mode ?? {};
}

export type AuditFilter = { limit?: number; aktion?: string; user_id?: string; seit?: string; q?: string };
export async function adminListAudit(limitOrFilter: number | AuditFilter = 200): Promise<AdminAuditEntry[]> {
  const f: AuditFilter = typeof limitOrFilter === 'number' ? { limit: limitOrFilter } : limitOrFilter;
  const qs = new URLSearchParams();
  qs.set('limit', String(f.limit ?? 200));
  if (f.aktion) qs.set('aktion', f.aktion);
  if (f.user_id) qs.set('user_id', f.user_id);
  if (f.seit) qs.set('seit', f.seit);
  if (f.q) qs.set('q', f.q);
  const r = await fetch(`${API}/admin/audit?${qs.toString()}`);
  if (!r.ok) return [];
  const data = await r.json();
  return data.entries ?? [];
}

// ─── Uploads (Multimodal) ─────────────────────────────────────────────

export type AudioTranscribe = {
  text: string;
  language: string;
  duration_ms: number;
  model: string;
};

export type ImageUpload = {
  file_id: string;
  filename: string;
  size_bytes: number;
  media_type: string;
};

export async function uploadAudio(blob: Blob, language = 'de'): Promise<AudioTranscribe> {
  const fd = new FormData();
  // Datei-Endung an die Browser-Aufnahme anpassen (meist webm/opus)
  const ext = blob.type.includes('webm') ? 'webm' : blob.type.includes('ogg') ? 'ogg' : 'wav';
  fd.append('audio', blob, `dictation.${ext}`);
  fd.append('language', language);
  const r = await fetch(`${API}/uploads/audio`, { method: 'POST', body: fd });
  if (!r.ok) throw new Error(`uploadAudio ${r.status}: ${await r.text()}`);
  return r.json();
}

export async function uploadImage(file: File): Promise<ImageUpload> {
  const fd = new FormData();
  fd.append('image', file, file.name);
  const r = await fetch(`${API}/uploads/image`, { method: 'POST', body: fd });
  if (!r.ok) throw new Error(`uploadImage ${r.status}: ${await r.text()}`);
  return r.json();
}

export type Unterlage = {
  file_id: string;
  filename: string;
  size_bytes: number;
  gelesen: boolean;
  quelle: string;
  zeichen: number;
  zuversicht?: number | null;
  hinweise: string[];
  vorschau: string;
};

/** Unterlage (PDF, Scan, Formular) für den Chat hochladen — wird serverseitig gelesen
 *  (PDF über Docling, Bild über Apple Vision OCR) und der Frage vorangestellt. */
export async function uploadUnterlage(file: File): Promise<Unterlage> {
  const fd = new FormData();
  fd.append('file', file, file.name);
  const r = await fetch(`${API}/uploads/unterlage`, { method: 'POST', body: fd });
  if (!r.ok) throw new Error(`uploadUnterlage ${r.status}: ${(await r.text()).slice(0, 200)}`);
  return r.json();
}

export type DocumentIngest = {
  patient_id: string;
  dateiname: string;
  gelesen: boolean;
  quelle: string;
  zeichen: number;
  messwerte: number;
  hinweise: string[];
  message: string;
};

export async function uploadDocument(patientId: string, file: File): Promise<DocumentIngest> {
  const fd = new FormData();
  fd.append('patient_id', patientId);
  fd.append('file', file, file.name);
  const r = await fetch(`${API}/uploads/document`, { method: 'POST', body: fd });
  if (!r.ok) throw new Error(`uploadDocument ${r.status}: ${await r.text()}`);
  return r.json();
}

export function uploadedImageUrl(fileId: string): string {
  return `${API}/uploads/image/${fileId}`;
}

export type SearchPhase = 'plan' | 'search' | 'synthesis';

/** Werkzeug-Aktivitaet waehrend des Streams (suche_befunde, grep_akte, …). */
export type ToolActivity = { name: string; phase: 'start' | 'result'; chars?: number };

export type SearchPlan = Record<string, string>;

export type SourceSearchResult = {
  source: string;
  query: string;
  chars: number;
  label: string;
};

/**
 * Streamt eine Chat-Antwort via SSE. Ruft onChunk pro Token, onDone am Ende.
 * onWarning für nicht-fatale Hinweise (z.B. Modell ohne Tool-Support).
 * onPhase + onPlan + onSourceSearch für Wissens-Modus-Pipeline-Progress.
 */
export async function streamMessage(
  chatId: string,
  content: string,
  onChunk: (text: string) => void,
  onDone: (meta?: any) => void,
  onError: (err: string) => void,
  temperature = 0.3,
  imageFileIds: string[] = [],
  enabledSources: string[] | null = null,
  onWarning: (warn: { content: string; model?: string }) => void = () => {},
  onPhase: (phase: SearchPhase, label: string) => void = () => {},
  onPlan: (queries: SearchPlan) => void = () => {},
  onSourceSearch: (result: SourceSearchResult) => void = () => {},
  onReviewCommit: (c: ReviewCommit) => void = () => {},
  onTool: (t: ToolActivity) => void = () => {},
  unterlageFileIds: string[] = []
): Promise<void> {
  const body: any = { content, temperature };
  if (imageFileIds.length > 0) body.image_file_ids = imageFileIds;
  if (unterlageFileIds.length > 0) body.unterlage_file_ids = unterlageFileIds;
  if (enabledSources !== null) body.enabled_sources = enabledSources;
  const resp = await fetch(`${API}/chats/${chatId}/messages/stream`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body)
  });
  if (!resp.ok || !resp.body) {
    onError(`stream ${resp.status}`);
    return;
  }
  const reader = resp.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    // SSE-Events sind durch \n\n getrennt
    const parts = buffer.split('\n\n');
    buffer = parts.pop() ?? '';
    for (const part of parts) {
      if (!part.startsWith('data: ')) continue;
      try {
        const evt = JSON.parse(part.slice(6));
        if (evt.type === 'chunk') onChunk(evt.content);
        else if (evt.type === 'done') { onDone(evt.meta); if (evt.review_commit) onReviewCommit(evt.review_commit); }
        else if (evt.type === 'error') onError(evt.content);
        else if (evt.type === 'warning') onWarning({ content: evt.content, model: evt.model });
        else if (evt.type === 'phase') onPhase(evt.phase, evt.label ?? '');
        else if (evt.type === 'plan') onPlan(evt.queries ?? {});
        else if (evt.type === 'source_search') onSourceSearch({
          source: evt.source, query: evt.query, chars: evt.chars, label: evt.label ?? evt.source
        });
        else if (evt.type === 'tool_start') onTool({ name: evt.name, phase: 'start' });
        else if (evt.type === 'tool_result') onTool({ name: evt.name, phase: 'result', chars: evt.chars });
      } catch {
        /* ignore parse errors */
      }
    }
  }
}

// ─── Review (Nachfragen — ersetzt Eingang + Prüfen) ───────────────────────────

export type ReviewQueueItem = {
  ref: string;
  source: 'inbox' | 'library' | 'hl7';
  title: string;
  recognized: Record<string, any>;
  chat_id: string | null;
};
export type ReviewCommit = { undo_token: string; undo_source: 'inbox' | 'library'; patient_id?: string | null };

export async function reviewQueue(): Promise<{ items: ReviewQueueItem[]; count: number }> {
  const r = await fetch(`${API}/review/queue`, { credentials: 'include' });
  if (!r.ok) return { items: [], count: 0 };
  return r.json();
}
/** Laborbefund ohne Zuordnung einer Akte zuweisen (Eingang). */
export async function hl7Zuordnen(ref: string, patientId: string, aliasMerken = true):
  Promise<{ ok: boolean; patient_id: string; laborwerte: number; dokumente: number }> {
  return _j(await fetch(`${API}/review/hl7/zuordnen`, {
    method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ref, patient_id: patientId, alias_merken: aliasMerken }),
  }), 'hl7Zuordnen');
}
export async function hl7Verwerfen(ref: string): Promise<{ ok: boolean }> {
  return _j(await fetch(`${API}/review/hl7/verwerfen`, {
    method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ref }),
  }), 'hl7Verwerfen');
}
export async function openReview(ref: string): Promise<string> {
  const r = await fetch(`${API}/review/open`, {
    method: 'POST', credentials: 'include',
    headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ ref }),
  });
  if (!r.ok) throw new Error(`openReview ${r.status}`);
  return (await r.json()).chat_id;
}
export async function reviewUndo(undo_token: string, source: 'inbox' | 'library'): Promise<void> {
  await fetch(`${API}/review/undo`, {
    method: 'POST', credentials: 'include',
    headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ undo_token, source }),
  });
}

// ─── Prüf-Formular (Review ohne KI-Chat) ─────────────────────────────

export type ReviewItemDetail = {
  source: 'inbox' | 'library';
  filename: string;
  patient_id?: string;
  recognized: Record<string, any>;
  pdf_url: string;
};

export async function getReviewItem(ref: string): Promise<ReviewItemDetail> {
  const r = await fetch(`${API}/review/item?ref=${encodeURIComponent(ref)}`, { credentials: 'include' });
  if (!r.ok) throw new Error(`getReviewItem ${r.status}`);
  return r.json();
}

export type ReviewCommitPayload = {
  ref: string;
  patient_id?: string;
  patient_meta?: { vorname?: string; nachname?: string; geburtsdatum?: string } | null;
  typ: string;
  datum?: string | null;
  fachgebiet?: string | null;
  diagnosen?: string[];
  medikamente?: string[];
  behandlung?: string[];
  kurz_summary?: string;
};

export async function reviewCommitForm(
  payload: ReviewCommitPayload
): Promise<{ ok: boolean; undo_token: string; undo_source: 'inbox' | 'library'; patient_id: string }> {
  const r = await fetch(`${API}/review/commit`, {
    method: 'POST', credentials: 'include',
    headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload),
  });
  if (!r.ok) {
    const detail = (await r.json().catch(() => null))?.detail;
    throw new Error(detail || `reviewCommit ${r.status}`);
  }
  return r.json();
}

export async function inviteUser(payload: {
  vorname: string; nachname: string; email: string; role: string;
}): Promise<{ ok: boolean; mail_sent: boolean; mail_fehler?: string; bestehendes_konto?: boolean; invite_url?: string; hinweis?: string }> {
  const r = await fetch(`${API}/users/invite`, {
    method: 'POST', credentials: 'include',
    headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload),
  });
  if (!r.ok) {
    const detail = (await r.json().catch(() => null))?.detail;
    throw new Error(detail || `inviteUser ${r.status}`);
  }
  return r.json();
}

// ─── SimED-Import (ZIP-Upload + Warteschlange + Status) ─────────────────────
export type ImportPatientResult = {
  folder: string;
  name?: string;
  pid?: string;
  action: 'neu' | 'ergänzt';
  match_reason: string;
  diagnosen: number;
  medikation: number;
  labor: number;
  journal_eintraege: number;
  docs_total: number;
  docs_ok: number;
  docs_failed: number;
  lab_values?: number;
  merged_unassigned?: number;
  msg_types?: string[];
  status: 'läuft' | 'ok' | 'error';
  message?: string;
};

export type ImportJob = {
  id: string;
  source: 'upload' | 'dropzone' | 'sftp';
  kind?: 'simed' | 'hl7';
  filename: string;
  size_bytes: number;
  status: 'queued' | 'running' | 'done' | 'error';
  created_at: string;
  created_by: string;
  started_at: string | null;
  finished_at: string | null;
  progress: { phase: string; current: number; total: number; patient: string };
  patients: ImportPatientResult[];
  counts: { created: number; merged: number; docs_ok: number; docs_failed: number; lab_values?: number; merged_unassigned?: number };
  log: string[];
  error: string | null;
};

export async function listImports(): Promise<ImportJob[]> {
  const r = await fetch(`${API}/imports`, { credentials: 'include' });
  if (!r.ok) throw new Error(`listImports ${r.status}`);
  return r.json();
}

export async function uploadImport(
  file: File,
  onProgress?: (pct: number) => void,
): Promise<{ id: string; status: string; filename: string }> {
  // XMLHttpRequest statt fetch — nur so gibt es Upload-Fortschritt für grosse ZIPs.
  return new Promise((resolve, reject) => {
    const fd = new FormData();
    fd.append('file', file, file.name);
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${API}/imports`);
    xhr.withCredentials = true;
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onProgress) onProgress(Math.round((e.loaded / e.total) * 100));
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try { resolve(JSON.parse(xhr.responseText)); } catch { reject(new Error('Ungültige Antwort')); }
      } else {
        let msg = `Upload fehlgeschlagen (${xhr.status})`;
        try { msg = JSON.parse(xhr.responseText)?.detail || msg; } catch {}
        reject(new Error(msg));
      }
    };
    xhr.onerror = () => reject(new Error('Netzwerkfehler beim Upload'));
    xhr.send(fd);
  });
}

export async function deleteImport(id: string): Promise<void> {
  const r = await fetch(`${API}/imports/${encodeURIComponent(id)}`, {
    method: 'DELETE', credentials: 'include',
  });
  if (!r.ok) {
    const detail = (await r.json().catch(() => null))?.detail;
    throw new Error(detail || `deleteImport ${r.status}`);
  }
}

export async function reviewAnalyze(payload: {
  ref: string; hint?: string; current?: Record<string, any>;
}): Promise<{ ok: boolean; fields: Record<string, any>; ocr_used: boolean }> {
  const r = await fetch(`${API}/review/analyze`, {
    method: 'POST', credentials: 'include',
    headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload),
  });
  if (!r.ok) {
    const detail = (await r.json().catch(() => null))?.detail;
    throw new Error(detail || `reviewAnalyze ${r.status}`);
  }
  return r.json();
}

export async function reviewDiscard(ref: string): Promise<void> {
  const r = await fetch(`${API}/review/discard`, {
    method: 'POST', credentials: 'include',
    headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ ref }),
  });
  if (!r.ok) throw new Error(`reviewDiscard ${r.status}`);
}

// ─── Chat-Sharing ─────────────────────────────────────────────────────

export type ChatShareInfo = {
  user_id: string;
  username: string | null;
  name: string;
  can_write: boolean;
  shared_at: string;
};

export async function shareChat(chatId: string, userIds: string[], canWrite: boolean): Promise<ChatShareInfo[]> {
  const r = await fetch(`${API}/chats/${chatId}/share`, {
    method: 'POST', credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ user_ids: userIds, can_write: canWrite }),
  });
  if (!r.ok) {
    const d = await r.json().catch(() => ({}));
    throw new Error(d.detail ?? `shareChat ${r.status}`);
  }
  return r.json();
}

export async function listChatShares(chatId: string): Promise<ChatShareInfo[]> {
  const r = await fetch(`${API}/chats/${chatId}/shares`, { credentials: 'include' });
  if (!r.ok) throw new Error(`listChatShares ${r.status}`);
  return r.json();
}

export async function unshareChat(chatId: string, userId: string): Promise<void> {
  const r = await fetch(`${API}/chats/${chatId}/share/${userId}`, {
    method: 'DELETE', credentials: 'include',
  });
  if (!r.ok) throw new Error(`unshareChat ${r.status}`);
}

// ─── Notifications ────────────────────────────────────────────────────

// Generische Glocke (seit 04.09.): chat_shared | chat_antwort | import_fertig | import_fehler.
// Klick springt auf `link`. Altfelder (chat_id …) bleiben fuer chat_shared gefuellt.
export type NotificationItem = {
  id: string;
  type: 'chat_shared' | 'chat_antwort' | 'import_fertig' | 'import_fehler' | string;
  titel: string;
  text: string;
  link: string | null;
  created_at: string;
  viewed: boolean;
  chat_id?: string | null;
  chat_title?: string | null;
  shared_by?: string | null;
  can_write?: boolean | null;
  shared_at?: string | null;
};

export async function listUnreadNotifications(): Promise<NotificationItem[]> {
  const r = await fetch(`${API}/notifications/unread`, { credentials: 'include' });
  if (!r.ok) return [];
  return r.json();
}

export async function deleteNotification(id: string): Promise<void> {
  await fetch(`${API}/notifications/${encodeURIComponent(id)}`, { method: 'DELETE', credentials: 'include' });
}

export async function deleteAllNotifications(): Promise<void> {
  await fetch(`${API}/notifications`, { method: 'DELETE', credentials: 'include' });
}

export async function markNotificationsViewed(link?: string): Promise<void> {
  // ohne link: alles gesehen; mit link: nur die Meldungen zur gerade offenen Seite
  await fetch(`${API}/notifications/mark-viewed`, {
    method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(link ? { link } : {}),
  });
}

// ─── Dashboard (Akten-zentrische Startseite) ──────────────────────────

export type DashboardRecentDoc = {
  patient_id: string;
  patient_name: string;
  filename: string;
  typ: string | null;
  datum: string | null;
  mtime: string;
};

export type DashboardRecentChat = {
  id: string;
  title: string;
  mode: ChatMode;
  patient_id: string | null;
  updated_at: string | null;
};

export type Dashboard = {
  patient_count: number;
  doc_count: number;
  pending_count: number;
  pending_items: { ref: string; source: 'inbox' | 'library'; title: string }[];
  recent_docs: DashboardRecentDoc[];
  recent_chats: DashboardRecentChat[];
};

export async function getDashboard(): Promise<Dashboard | null> {
  const r = await fetch(`${API}/dashboard`, { credentials: 'include' });
  if (!r.ok) return null;
  return r.json();
}

// ─── Web-Push (PWA-Benachrichtigungen) ────────────────────────────────

export async function pushVapidKey(): Promise<string> {
  const r = await fetch(`${API}/push/vapid-key`, { credentials: 'include' });
  if (!r.ok) return '';
  return (await r.json()).public_key || '';
}

export async function pushSubscribe(sub: PushSubscriptionJSON): Promise<boolean> {
  const r = await fetch(`${API}/push/subscribe`, {
    method: 'POST', credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ endpoint: sub.endpoint, keys: sub.keys ?? {} }),
  });
  return r.ok;
}

export async function pushUnsubscribe(sub: PushSubscriptionJSON): Promise<void> {
  await fetch(`${API}/push/unsubscribe`, {
    method: 'POST', credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ endpoint: sub.endpoint, keys: sub.keys ?? {} }),
  });
}

export async function pushTest(): Promise<number> {
  const r = await fetch(`${API}/push/test`, { method: 'POST', credentials: 'include' });
  if (!r.ok) return 0;
  return (await r.json()).sent ?? 0;
}


// ─── Mandanten & Branding (PowerCare) ───────────────────────────────────
export type Mandant = { slug: string; name: string; aktiv: boolean; branding: Record<string, any>; quellen: Record<string, any>; db: string; data_root: string; platte?: { status: string; gb?: number; meldung?: string } };
async function _j<T>(r: Response, was: string): Promise<T> {
  if (!r.ok) { const d = await r.json().catch(() => ({})); throw new Error((d as any).detail || `${was} ${r.status}`); }
  return r.json();
}
export type MandantNutzer = {
  id: string | null; username: string | null; email: string | null; name: string | null;
  rolle: string | null; aktiv: boolean | null;
  letzter_login: string | null; je_angemeldet: boolean;
  powerid: { vorhanden: boolean; aktiv: boolean | null; in_zugang: boolean | null; in_admin: boolean | null } | null;
  hinweise: string[];
};
export type MandantNutzerAntwort = {
  slug: string;
  powerid: { erreichbar: boolean; grund: string | null };
  gruppen: { zugang: string; zugang_da: boolean; admin: string; admin_da: boolean };
  nutzer: MandantNutzer[];
};
export async function mandantNutzer(slug: string): Promise<MandantNutzerAntwort> {
  return _j(await fetch(`${API}/mandanten/${encodeURIComponent(slug)}/nutzer`, { credentials: 'include' }), 'mandantNutzer');
}
export async function listMandanten(): Promise<{ mandanten: Mandant[]; aktuell: string | null }> {
  return _j(await fetch(`${API}/mandanten`, { credentials: 'include' }), 'listMandanten');
}
export async function createMandant(slug: string, name: string, gb = 20): Promise<Mandant> {
  return _j(await fetch(`${API}/mandanten`, { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ slug, name, gb }) }), 'createMandant');
}
export async function updateMandant(slug: string, payload: { name?: string; aktiv?: boolean; branding?: Record<string, any>; quellen?: Record<string, any> }): Promise<Mandant> {
  return _j(await fetch(`${API}/mandanten/${encodeURIComponent(slug)}`, { method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) }), 'updateMandant');
}
export async function uploadMandantLogo(slug: string, file: File): Promise<Mandant> {
  const fd = new FormData(); fd.append('file', file);
  return _j(await fetch(`${API}/mandanten/${encodeURIComponent(slug)}/logo`, { method: 'POST', credentials: 'include', body: fd }), 'uploadMandantLogo');
}
export async function uploadMandantFavicon(slug: string, file: File): Promise<Mandant> {
  const fd = new FormData(); fd.append('file', file);
  return _j(await fetch(`${API}/mandanten/${encodeURIComponent(slug)}/favicon`, { method: 'POST', credentials: 'include', body: fd }), 'uploadMandantFavicon');
}
export async function deleteMandantFavicon(slug: string): Promise<Mandant> {
  return _j(await fetch(`${API}/mandanten/${encodeURIComponent(slug)}/favicon`, { method: 'DELETE', credentials: 'include' }), 'deleteMandantFavicon');
}
export async function deleteMandantLogo(slug: string): Promise<Mandant> {
  return _j(await fetch(`${API}/mandanten/${encodeURIComponent(slug)}/logo`, { method: 'DELETE', credentials: 'include' }), 'deleteMandantLogo');
}

// ─── Arbeitstisch (#2/#3, 09.10.2026) ─────────────────────────────────────────
// Dokument rein, Text raus — ohne Patient, ohne Internet, ohne Speichern. Der Verlauf lebt nur im Fenster.

export type ArbeitstischAufgabe = { id: string; name: string; anweisung: string };
export type ArbeitstischRunde = { role: 'user' | 'assistant'; content: string };

export async function arbeitstischAufgaben(): Promise<ArbeitstischAufgabe[]> {
  const r = await fetch(`${API}/arbeitstisch/aufgaben`, { credentials: 'include' });
  if (!r.ok) throw new Error(`arbeitstischAufgaben ${r.status}`);
  return r.json();
}

export async function arbeitstischAufgabenSpeichern(liste: ArbeitstischAufgabe[]): Promise<ArbeitstischAufgabe[]> {
  const r = await fetch(`${API}/arbeitstisch/aufgaben`, {
    method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(liste),
  });
  if (!r.ok) throw new Error((await r.json().catch(() => null))?.detail ?? `Speichern ${r.status}`);
  return r.json();
}

export async function arbeitstischAufgabenZuruecksetzen(): Promise<ArbeitstischAufgabe[]> {
  const r = await fetch(`${API}/arbeitstisch/aufgaben`, { method: 'DELETE' });
  if (!r.ok) throw new Error(`Zurücksetzen ${r.status}`);
  return r.json();
}

/** Streamt eine Arbeitstisch-Antwort. `onKontext` liefert die eigene Nachricht (inkl. gelesenem Unterlagentext)
 *  für Folgefragen — die Datei selbst ist auf dem Server da schon gelöscht. */
export async function streamArbeitstisch(
  body: { aufgabe_id?: string | null; text?: string; unterlage_file_ids?: string[]; verlauf?: ArbeitstischRunde[] },
  onKontext: (k: string) => void,
  onChunk: (t: string) => void,
  onDone: () => void,
  onError: (e: string) => void,
): Promise<void> {
  const resp = await fetch(`${API}/arbeitstisch/stream`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
  });
  if (!resp.ok || !resp.body) {
    onError((await resp.json().catch(() => null))?.detail ?? `Fehler ${resp.status}`);
    return;
  }
  const reader = resp.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let fertig = false;
  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const parts = buffer.split('\n\n');
    buffer = parts.pop() ?? '';
    for (const part of parts) {
      if (!part.startsWith('data: ')) continue;
      try {
        const evt = JSON.parse(part.slice(6));
        if (evt.type === 'kontext') onKontext(evt.content);
        else if (evt.type === 'chunk') onChunk(evt.content);
        else if (evt.type === 'done') { fertig = true; onDone(); }
        else if (evt.type === 'error') { fertig = true; onError(evt.content); }
      } catch { /* ignore */ }
    }
  }
  if (!fertig) onError('Die Verbindung brach ab. Bitte erneut versuchen.');
}

export type ArbeitstischGrundanweisung = { text: string; standard: boolean };

export async function arbeitstischGrundanweisung(): Promise<ArbeitstischGrundanweisung> {
  const r = await fetch(`${API}/arbeitstisch/grundanweisung`);
  if (!r.ok) throw new Error(`Grundanweisung ${r.status}`);
  return r.json();
}

export async function arbeitstischGrundanweisungSpeichern(text: string): Promise<ArbeitstischGrundanweisung> {
  const r = await fetch(`${API}/arbeitstisch/grundanweisung`, {
    method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ text }),
  });
  if (!r.ok) throw new Error((await r.json().catch(() => null))?.detail ?? `Speichern ${r.status}`);
  return r.json();
}

export async function arbeitstischGrundanweisungZuruecksetzen(): Promise<ArbeitstischGrundanweisung> {
  const r = await fetch(`${API}/arbeitstisch/grundanweisung`, { method: "DELETE" });
  if (!r.ok) throw new Error(`Zurücksetzen ${r.status}`);
  return r.json();
}
